// belts.ts - the main asteroid belt and Kuiper belt as instanced particle rings.
//
// Not a simulation. Each particle is drawn once, at mount, from its belt's
// own distribution (beltData.ts) and frozen at that heliocentric point:
// nobody tracks a hundred thousand real orbits, and a field this size does
// not need to move to read as a belt rather than a wall of dust. What moves
// is the camera, through the same `frame.register` every other heliocentric
// decoration uses - each belt is registered at the origin, the way a
// heliocentric orbit ring is (orrery.ts), since every particle's own offset
// from Sol is already baked into its vertex position.
//
// Each belt is one `THREE.Points` object: one draw call however many
// instances, so the ORBIT band's own draw-call budget never sees more than
// the two this module adds.

import * as THREE from 'three';

import { kepler, AU_KM } from '../world/ephemeris.ts';
import { eclipticToScene, UNITS_PER_KM } from './orrery.ts';
import type { WorldFrameHandle, WorldVec } from '../world/frame';
import { BELTS, BELT_INSTANCE_CAP, type BeltDef } from './beltData.ts';

const DEG = Math.PI / 180;
const ORIGIN: WorldVec = { x: 0, y: 0, z: 0 };

/** How long a switch takes to fully fade, ms: a look only, never what the
 *  instruments read - `points.visible` flips with the switch itself, so a
 *  panel row reads on/off on the same frame that the app's own hex and ship
 *  rows do, and only the opacity behind it eases. */
const FADE_MS = 500;

/** FNV-1a: a tiny string hash, so each belt's own particle field is stable
 *  across reloads without a shared PRNG state between them. */
function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Mulberry32: tiny, seeded, deterministic - the belt looks the same on every load. */
function makeRand(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), a | 1);
    t = (t + Math.imul(t ^ (t >>> 7), t | 61)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A half-normal draw: mode 0, resampled past `capDeg` rather than clipped
 *  onto it, so the shape under the cap stays the distribution's own. */
function halfNormalDeg(rand: () => number, sigmaDeg: number, capDeg: number): number {
  for (let i = 0; i < 64; i++) {
    const u1 = Math.max(rand(), 1e-9);
    const u2 = rand();
    const z = Math.abs(Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2));
    const deg = z * sigmaDeg;
    if (deg <= capDeg) return deg;
  }
  return capDeg;
}

/** A Rayleigh draw, mode = sigma, resampled past `cap` the same way. */
function rayleigh(rand: () => number, sigma: number, cap: number): number {
  for (let i = 0; i < 64; i++) {
    const u = Math.max(rand(), 1e-9);
    const v = sigma * Math.sqrt(-2 * Math.log(1 - u));
    if (v <= cap) return v;
  }
  return cap;
}

/** Semi-major axis by rejection sampling against the belt's own density. */
function sampleAxisAu(rand: () => number, belt: BeltDef): number {
  const lo = belt.innerAu - belt.padAu;
  const hi = belt.outerAu + belt.padAu;
  for (let i = 0; i < 200; i++) {
    const a = lo + rand() * (hi - lo);
    if (rand() * belt.densityPeak <= belt.densityAt(a)) return a;
  }
  return (belt.innerAu + belt.outerAu) * 0.5;
}

/**
 * One particle's heliocentric position, AU, ecliptic J2000 - the same
 * element-to-Cartesian rotation `ephemeris.ts`'s `stateAt` uses, reused via
 * its exported `kepler`, with the particle's own drawn axis, eccentricity and
 * inclination and a uniformly random node and mean anomaly.
 *
 * The argument of periapsis is folded into the random node instead of drawn
 * on its own: with the anomaly already uniform, a fixed periapsis direction
 * per particle is statistically indistinguishable from a drawn one once
 * thousands of particles carry their own random node, and the belt has no
 * preferred periapsis to cite anyway.
 */
function particlePositionAu(
  aAu: number, e: number, iDeg: number, nodeDeg: number, mDeg: number,
): { x: number; y: number; z: number } {
  const E = kepler(mDeg * DEG, e);
  const cosE = Math.cos(E);
  const sinE = Math.sin(E);
  const flat = Math.sqrt(Math.max(0, 1 - e * e));
  const xp = aAu * (cosE - e);
  const yp = aAu * flat * sinE;
  const nRad = nodeDeg * DEG;
  const iRad = iDeg * DEG;
  const cn = Math.cos(nRad);
  const sn = Math.sin(nRad);
  const ci = Math.cos(iRad);
  const si = Math.sin(iRad);
  return { x: cn * xp - sn * ci * yp, y: sn * xp + cn * ci * yp, z: si * yp };
}

/** A soft round dot, so a belt reads as dust rather than a field of squares. */
function dotTexture(): THREE.CanvasTexture {
  const size = 16;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  if (g) {
    const r = size / 2;
    const grad = g.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  }
  return new THREE.CanvasTexture(c);
}

interface BuiltBelt {
  readonly positions: Float32Array;
  readonly count: number;
  readonly minAAu: number;
  readonly maxAAu: number;
}

function buildBelt(def: BeltDef): BuiltBelt {
  const rand = makeRand(hashSeed(def.id));
  const n = Math.min(def.count, BELT_INSTANCE_CAP);
  const positions = new Float32Array(n * 3);
  let minAAu = Infinity;
  let maxAAu = -Infinity;
  for (let i = 0; i < n; i++) {
    const aAu = sampleAxisAu(rand, def);
    if (aAu < minAAu) minAAu = aAu;
    if (aAu > maxAAu) maxAAu = aAu;
    const e = rayleigh(rand, def.eccentricitySigma, def.eccentricityCap);
    const iDeg = halfNormalDeg(rand, def.inclinationSigmaDeg, def.inclinationCapDeg);
    const nodeDeg = rand() * 360;
    const mDeg = rand() * 360;
    const posAu = particlePositionAu(aAu, e, iDeg, nodeDeg, mDeg);
    const scene = eclipticToScene(
      { x: posAu.x * AU_KM, y: posAu.y * AU_KM, z: posAu.z * AU_KM }, UNITS_PER_KM,
    );
    positions[i * 3] = scene.x;
    positions[i * 3 + 1] = scene.y;
    positions[i * 3 + 2] = scene.z;
  }
  return { positions, count: n, minAAu, maxAAu };
}

interface Belt {
  readonly id: string;
  readonly label: string;
  readonly def: BeltDef;
  readonly points: THREE.Points;
  readonly material: THREE.PointsMaterial;
  readonly built: BuiltBelt;
}

export interface BeltsConfig {
  /** The camera-relative frame the orrery's own bodies are registered in. */
  frame: WorldFrameHandle;
  onNeedsRender: () => void;
}

export interface BeltsHandle {
  /** SYSTEM/ORBIT only: the player's own switch, ANDed with the band gate
   *  the ladder drives every frame, composing rather than fighting it - the
   *  way `orrery.setLayerVisible` and `shipMarker.setLayerVisible` do. */
  setLayerVisible(on: boolean): void;
  setBandVisible(on: boolean): void;
  /** `descent-dojo --belts --inject belts-suppress`: every belt drawn empty. */
  setSuppressed(on: boolean): void;
  /** Whether the belts are drawn this frame - the switch state, not the eased
   *  opacity, so an instrument reading this sees the same frame the switch
   *  itself acted on. */
  readonly visible: boolean;
  update(): void;
  dispose(): void;
  /** For the instruments: what each belt actually holds and draws. */
  debug(): unknown;
}

export function createBelts(config: BeltsConfig): BeltsHandle {
  const teardown: Array<() => void> = [];
  const own = (fn: () => void): void => { teardown.push(fn); };
  const dot = dotTexture();
  own(() => dot.dispose());

  let layerOn = true;
  let bandOn = false;
  let suppressed = false;
  let shown = false;
  let opacity = 0;
  let lastMs = 0;

  const belts: Belt[] = BELTS.map((def) => {
    const built = buildBelt(def);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(built.positions, 3));
    const material = new THREE.PointsMaterial({
      color: new THREE.Color(def.colorCss), size: def.pointSizePx, sizeAttenuation: false,
      map: dot, transparent: true, depthWrite: false, opacity: 0,
    });
    const points = new THREE.Points(geo, material);
    points.name = `belt-${def.id}`;
    // Registered camera-relative like a heliocentric orbit ring: the object's
    // own position is rewritten every frame and a stale cull against last
    // frame's matrix is exactly what orrery.ts disables this for too.
    points.frustumCulled = false;
    points.visible = false;
    const unregister = config.frame.register(points, () => ORIGIN);
    own(() => {
      unregister();
      geo.dispose();
      material.dispose();
    });
    return { id: def.id, label: def.label, def, points, material, built };
  });

  function update(): void {
    const now = performance.now();
    const dt = lastMs === 0 ? 16 : Math.min(100, now - lastMs);
    lastMs = now;
    shown = layerOn && bandOn && !suppressed;
    const target = shown ? 1 : 0;
    opacity += (target - opacity) * Math.min(1, dt / FADE_MS);
    if (Math.abs(target - opacity) < 0.002) opacity = target;
    for (const b of belts) {
      if (b.points.visible !== shown) { b.points.visible = shown; config.onNeedsRender(); }
      if (b.material.opacity !== opacity) {
        b.material.opacity = opacity;
        config.onNeedsRender();
      }
    }
  }

  return {
    setLayerVisible(on: boolean): void {
      if (layerOn === on) return;
      layerOn = on;
      config.onNeedsRender();
    },
    setBandVisible(on: boolean): void {
      if (bandOn === on) return;
      bandOn = on;
      config.onNeedsRender();
    },
    setSuppressed(on: boolean): void {
      if (suppressed === on) return;
      suppressed = on;
      config.onNeedsRender();
    },
    get visible(): boolean { return shown; },
    update,
    dispose(): void {
      for (let i = teardown.length - 1; i >= 0; i--) {
        try { teardown[i]!(); } catch { /* a failed release must not skip the rest */ }
      }
      teardown.length = 0;
    },
    debug(): unknown {
      return {
        suppressed,
        visible: shown,
        opacity: +opacity.toFixed(3),
        belts: belts.map((b) => ({
          id: b.id,
          label: b.label,
          count: b.built.count,
          cap: BELT_INSTANCE_CAP,
          innerAu: b.def.innerAu,
          outerAu: b.def.outerAu,
          padAu: b.def.padAu,
          minAAu: +b.built.minAAu.toFixed(4),
          maxAAu: +b.built.maxAAu.toFixed(4),
          // The real object's own flag, not the cached switch state: a bug that
          // computes `shown` correctly but never applies it would still read
          // true here.
          visible: b.points.visible,
          opacity: +b.material.opacity.toFixed(3),
          drawCalls: 1,
        })),
      };
    },
  };
}
